import React, { useEffect, useState, useMemo, useRef } from 'react';
import { createPortal } from 'react-dom';
import { useDispatch, useSelector } from 'react-redux';
import { clearFormError } from 'js/actions';
import {
  LinearProgress,
  linearProgressClasses,
  Paper,
  Slide,
  Snackbar,
  Stack,
  styled,
} from '@mui/material';
import { ErrorOutline, Close as CloseIcon } from '@mui/icons-material';
import Typography from 'new/elements/Typography';
import { IconButton } from 'new/elements/IconButton/IconButton';
import { interpolateCSSVariable } from 'new/theme';
import { TransitionProps } from '@mui/material/transitions';
import classes from './FormErrorDisplay.module.scss';

// Creating our own ERROR_TIMEOUT to sync animations with legacy ErrorDisplay
const ERROR_TIMEOUT = 4400;

export interface FormErrorDisplayProps {
  formType: string;
}

const LinearTimerView = styled(LinearProgress)(() => ({
  height: 6,
  borderRadius: 3,
  [`&.${linearProgressClasses.colorPrimary}`]: {
    backgroundColor: 'transparent',
  },
  [`& .${linearProgressClasses.bar}`]: {
    borderRadius: 3,
    transition: 'transform 0.1s linear',
    backgroundColor: interpolateCSSVariable('--color-danger-700'),
  },
}));

function TransitionLeft(props: TransitionProps) {
  return <Slide {...props} direction="left" />;
}

function FormErrorDisplay({ formType }: FormErrorDisplayProps) {
  const dispatch = useDispatch();

  // Get error from Redux store
  const error = useSelector((state: any) => state.forms[formType]?.error);
  const dialogIsOpen = useSelector((state: any) => state.forms[formType]?.dialogIsOpen);

  const [progress, setProgress] = useState(100);
  const [isVisible, setIsVisible] = useState(false);
  const intervalRef = useRef<NodeJS.Timeout | null>(null);

  const errorStatus = useMemo(() => error && !dialogIsOpen, [error, dialogIsOpen]);

  // Clear interval on unmount
  useEffect(() => {
    return () => {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
      }
    };
  }, []);

  // Reset progress when error appears
  useEffect(() => {
    if (errorStatus) {
      setProgress(100);
      setIsVisible(true);
    } else {
      setIsVisible(false);
    }
  }, [errorStatus]);

  // Auto-dismiss timer
  useEffect(() => {
    if (!isVisible || progress <= 0) return;

    intervalRef.current = setInterval(() => {
      setProgress((p) => {
        const remaining = (p * ERROR_TIMEOUT) / 100;
        const newRemaining = remaining - 100;
        return Math.max(0, (newRemaining * 100) / ERROR_TIMEOUT);
      });
    }, 100);

    return () => {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
        intervalRef.current = null;
      }
    };
  }, [isVisible, progress]);

  // Close when progress reaches 0
  useEffect(() => {
    if (progress <= 0 && isVisible) {
      // Small delay for smooth animation
      setTimeout(() => {
        handleClose();
      }, 200);
    }
  }, [progress, isVisible]);

  const handleClose = () => {
    dispatch(clearFormError(formType));
  };

  // Pause timer on hover
  const handleMouseEnter = () => {
    if (intervalRef.current) {
      clearInterval(intervalRef.current);
      intervalRef.current = null;
    }
  };

  // Resume timer on mouse leave
  const handleMouseLeave = () => {
    if (isVisible && progress > 0 && !intervalRef.current) {
      intervalRef.current = setInterval(() => {
        setProgress((p) => {
          const remaining = (p * ERROR_TIMEOUT) / 100;
          const newRemaining = remaining - 100;
          return Math.max(0, (newRemaining * 100) / ERROR_TIMEOUT);
        });
      }, 100);
    }
  };

  const handleImmediateClose = () => {
    setProgress(0);
  };

  if (!errorStatus || !error) {
    return null;
  }

  const content = (
    <Snackbar
      data-testid="form-error-display"
      anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      open={progress > 0}
      TransitionComponent={TransitionLeft}
    >
      <Paper elevation={6} className={classes.container}>
        <Stack onMouseEnter={handleMouseEnter} onMouseLeave={handleMouseLeave}>
          <LinearTimerView variant="determinate" value={progress} />
          <Stack direction="row" marginTop="5px" alignItems="flex-start">
            <ErrorOutline
              sx={{
                color: 'var(--color-danger-700)',
                marginTop: 'var(--spacing-xs)',
              }}
            />

            <Stack marginLeft="10px" width="500px" flex={1}>
              <Stack direction="row" justifyContent="space-between" alignItems="center">
                <Typography variant="subtitle1">Error</Typography>
                <IconButton onClick={handleImmediateClose} aria-label="Close error">
                  <CloseIcon />
                </IconButton>
              </Stack>
              <Typography className={classes.message} variant="body2">
                {error}
              </Typography>
            </Stack>
          </Stack>
        </Stack>
      </Paper>
    </Snackbar>
  );

  return createPortal(<div role="alert">{content}</div>, document.body);
}

export default FormErrorDisplay;
